import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import {
  Settings,
  Clock,
  Save,
  Loader2,
  MessageSquare,
  Zap,
  Plus,
  Trash2,
  X,
  Hand,
  Star,
  Image as ImageIcon,
  Timer,
  ShieldCheck,
  ChevronRight,
  ToggleLeft,
  CalendarDays
} from 'lucide-react';
import {
  getAdminSettings,
  updateAdminSettings,
  getQuickReplies,
  createQuickReply,
  deleteQuickReply,
  getSystemStatus,
  toggleWebhookTrust,
  uploadFile
} from '../lib/api';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Segunda-feira', short: 'Seg' },
  { value: 2, label: 'Terça-feira', short: 'Ter' },
  { value: 3, label: 'Quarta-feira', short: 'Qua' },
  { value: 4, label: 'Quinta-feira', short: 'Qui' },
  { value: 5, label: 'Sexta-feira', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 7, label: 'Domingo', short: 'Dom' },
];

const DEFAULT_SCHEDULE = {
  "1": { enabled: true, start: "08:00", end: "18:00" },
  "2": { enabled: true, start: "08:00", end: "18:00" },
  "3": { enabled: true, start: "08:00", end: "18:00" },
  "4": { enabled: true, start: "08:00", end: "18:00" },
  "5": { enabled: true, start: "08:00", end: "18:00" },
  "6": { enabled: false, start: "08:00", end: "12:00" },
  "7": { enabled: false, start: "00:00", end: "00:00" }
};

/* ─── Sidebar nav items ─── */
const NAV_ITEMS = [
  { id: 'messages', label: 'Mensagens', icon: MessageSquare, description: 'Boas-vindas e encerramento' },
  { id: 'hours', label: 'Horários', icon: Clock, description: 'Dias e horário comercial' },
  { id: 'quick', label: 'Respostas Rápidas', icon: Zap, description: 'Atalhos de mensagens' },
  { id: 'survey', label: 'Pesquisa', icon: Star, description: 'Satisfação do cliente' },
  { id: 'system', label: 'Sistema', icon: ShieldCheck, description: 'Timeouts e regras' },
];

/* ─── Reusable section wrapper ─── */
const SettingsSection = ({ icon: Icon, title, description, children, badge }) => (
  <div className="space-y-5">
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="text-base font-semibold font-mono">{title}</h3>
          {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      {badge}
    </div>
    <div className="space-y-4 pl-12">
      {children}
    </div>
  </div>
);

/* ─── Inline toggle row ─── */
const ToggleRow = ({ label, description, checked, onCheckedChange, disabled }) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border border-border/50 bg-card/50 p-4">
    <div className="space-y-0.5">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
  </div>
);

/* ─── Field group ─── */
const FieldGroup = ({ label, description, children }) => (
  <div className="space-y-2">
    <Label className="text-sm">{label}</Label>
    {description && <p className="text-xs text-muted-foreground">{description}</p>}
    {children}
  </div>
);

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('messages');

  const [settings, setSettings] = useState({
    welcome_message_enabled: false,
    welcome_message: 'Olá! 👋 Seja bem-vindo(a)! Em que posso ajudar?',
    welcome_message_image: '',
    business_hours_enabled: false,
    business_hours_schedule: { ...DEFAULT_SCHEDULE },
    business_hours_message: 'Olá! No momento estamos fora do horário de atendimento. Retornaremos em breve!',
    closing_message_enabled: true,
    closing_message: 'Atendimento finalizado. Obrigado pelo contato! 😊',
    satisfaction_survey_enabled: false,
    satisfaction_survey_message: 'Como você avalia nosso atendimento? Responda com um número de 1 a 5:\n\n⭐ 1 - Muito ruim\n⭐⭐ 2 - Ruim\n⭐⭐⭐ 3 - Regular\n⭐⭐⭐⭐ 4 - Bom\n⭐⭐⭐⭐⭐ 5 - Excelente',
    satisfaction_thank_you_message: 'Obrigado pelo seu feedback! Sua opinião é muito importante para nós. 🙏',
    satisfaction_timeout_minutes: 10,
    bot_timeout_minutes: 0,
    bot_timeout_message_enabled: false,
    bot_timeout_message: 'Olá! Notamos que você não respondeu. Estamos transferindo seu atendimento para um atendente humano. Aguarde um momento! 😊',
    promessa_dias: 5,
    attendance_timeout_minutes: 0,
    timeout_message_enabled: false,
    timeout_message: 'Atendimento encerrado por inatividade. Se precisar de ajuda novamente, é só responder esta mensagem.',
    delete_conversations_enabled: true,
    close_delay_seconds: 60,
  });

  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplyDialog, setShowQuickReplyDialog] = useState(false);
  const [newQuickReply, setNewQuickReply] = useState({ shortcut: '', title: '', content: '' });
  const [savingQuickReply, setSavingQuickReply] = useState(false);
  const [welcomeImageUploading, setWelcomeImageUploading] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [togglingTrust, setTogglingTrust] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadSettings(), loadQuickReplies(), loadSystemStatus()]);
    setLoading(false);
  };

  const loadSettings = async () => {
    try {
      const response = await getAdminSettings();
      let schedule = response.data.business_hours_schedule;
      if (!schedule) {
        const legacyDays = response.data.business_hours_days || [1, 2, 3, 4, 5];
        const legacyStart = response.data.business_hours_start || '08:00';
        const legacyEnd = response.data.business_hours_end || '18:00';
        schedule = { ...DEFAULT_SCHEDULE };
        DAYS_OF_WEEK.forEach(day => {
          schedule[String(day.value)] = { enabled: legacyDays.includes(day.value), start: legacyStart, end: legacyEnd };
        });
      }
      setSettings({
        welcome_message_enabled: response.data.welcome_message_enabled || false,
        welcome_message: response.data.welcome_message || 'Olá! 👋 Seja bem-vindo(a)! Em que posso ajudar?',
        welcome_message_image: response.data.welcome_message_image || '',
        business_hours_enabled: response.data.business_hours_enabled || false,
        business_hours_schedule: schedule,
        business_hours_message: response.data.business_hours_message || 'Olá! No momento estamos fora do horário de atendimento. Retornaremos em breve!',
        closing_message_enabled: response.data.closing_message_enabled !== false,
        closing_message: response.data.closing_message || 'Atendimento finalizado. Obrigado pelo contato! 😊',
        satisfaction_survey_enabled: response.data.satisfaction_survey_enabled || false,
        satisfaction_survey_message: response.data.satisfaction_survey_message || 'Como você avalia nosso atendimento?...',
        satisfaction_thank_you_message: response.data.satisfaction_thank_you_message || 'Obrigado pelo seu feedback!...',
        satisfaction_timeout_minutes: response.data.satisfaction_timeout_minutes || 10,
        bot_timeout_minutes: response.data.bot_timeout_minutes || 0,
        bot_timeout_message_enabled: response.data.bot_timeout_message_enabled || false,
        bot_timeout_message: response.data.bot_timeout_message || 'Olá! Notamos que você não respondeu. Estamos transferindo seu atendimento para um atendente humano. Aguarde um momento! 😊',
        promessa_dias: response.data.promessa_dias || 5,
        attendance_timeout_minutes: response.data.attendance_timeout_minutes || 0,
        timeout_message_enabled: response.data.timeout_message_enabled || false,
        timeout_message: response.data.timeout_message || 'Atendimento encerrado por inatividade. Se precisar de ajuda novamente, é só responder esta mensagem.',
        delete_conversations_enabled: response.data.delete_conversations_enabled !== false,
        close_delay_seconds: response.data.close_delay_seconds || 60,
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadQuickReplies = async () => {
    try { const r = await getQuickReplies(); setQuickReplies(r.data); } catch (e) { console.error(e); }
  };
  const loadSystemStatus = async () => {
    try { const r = await getSystemStatus(); setSystemStatus(r.data); } catch (e) { console.error(e); }
  };

  const handleWelcomeImageUpload = async (file) => {
    if (!file) return;
    setWelcomeImageUploading(true);
    try {
      const response = await uploadFile(file);
      if (response?.data?.url) {
        setSettings({ ...settings, welcome_message_image: response.data.url });
        toast.success('Imagem enviada com sucesso!');
      } else { toast.error('Não foi possível enviar a imagem'); }
    } catch (error) { console.error(error); toast.error('Erro ao enviar imagem'); }
    finally { setWelcomeImageUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminSettings(settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) { console.error(error); toast.error('Erro ao salvar configurações'); }
    finally { setSaving(false); }
  };

  const handleCreateQuickReply = async () => {
    if (!newQuickReply.shortcut || !newQuickReply.content) { toast.error('Preencha o atalho e o conteúdo'); return; }
    setSavingQuickReply(true);
    try {
      await createQuickReply(newQuickReply);
      toast.success('Resposta rápida criada!');
      setShowQuickReplyDialog(false);
      setNewQuickReply({ shortcut: '', title: '', content: '' });
      loadQuickReplies();
    } catch (error) { console.error(error); toast.error('Erro ao criar resposta rápida'); }
    finally { setSavingQuickReply(false); }
  };

  const handleDeleteQuickReply = async (id) => {
    try { await deleteQuickReply(id); toast.success('Resposta rápida excluída'); loadQuickReplies(); }
    catch (error) { console.error(error); toast.error('Erro ao excluir'); }
  };

  const handleToggleWebhookTrust = async () => {
    setTogglingTrust(true);
    try { const r = await toggleWebhookTrust(); toast.success(r.data.message); loadSystemStatus(); }
    catch (error) { console.error(error); toast.error('Erro ao alterar configuração'); }
    finally { setTogglingTrust(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  /* ─────────────── RENDER ─────────────── */
  return (
    <div className="flex-1 overflow-hidden flex flex-col h-full">
      {/* ── Top bar ── */}
      <div className="shrink-0 border-b border-border/50 bg-card/30 backdrop-blur-sm px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Settings size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold font-mono">Configurações</h1>
              <p className="text-xs text-muted-foreground">Gerencie mensagens, horários e regras do sistema</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2 px-5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 overflow-hidden flex">
        {/* ── Sidebar ── */}
        <nav className="shrink-0 w-56 border-r border-border/50 overflow-y-auto py-4 px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 group ${isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`}
              >
                <Icon size={16} className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'} />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium block truncate ${isActive ? 'text-primary' : ''}`}>{item.label}</span>
                </div>
                {isActive && <ChevronRight size={14} className="text-primary/60 shrink-0" />}
              </button>
            );
          })}
        </nav>

        {/* ── Main panel ── */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-8 py-8 pb-24 space-y-10">

            {/* ═══════ MENSAGENS ═══════ */}
            {activeSection === 'messages' && (
              <>
                {/* Welcome message */}
                <SettingsSection
                  icon={Hand}
                  title="Mensagem Inicial"
                  description="Enviada automaticamente quando um cliente inicia uma nova conversa"
                  badge={
                    <Badge variant={settings.welcome_message_enabled ? 'default' : 'secondary'} className="text-xs">
                      {settings.welcome_message_enabled ? 'Ativa' : 'Inativa'}
                    </Badge>
                  }
                >
                  <ToggleRow
                    label="Habilitar mensagem inicial"
                    description="O cliente recebe esta mensagem ao iniciar uma conversa ou após um encerramento."
                    checked={settings.welcome_message_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, welcome_message_enabled: checked })}
                  />

                  <FieldGroup label="Texto da mensagem">
                    <Textarea
                      placeholder="Olá! 👋 Seja bem-vindo(a)! Em que posso ajudar?"
                      value={settings.welcome_message}
                      onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                      disabled={!settings.welcome_message_enabled}
                      rows={3}
                      className="resize-none"
                    />
                  </FieldGroup>

                  <FieldGroup label="Imagem da mensagem" description="Informe uma URL ou faça upload da imagem">
                    <Input
                      placeholder="https://exemplo.com/imagem.jpg"
                      value={settings.welcome_message_image}
                      onChange={(e) => setSettings({ ...settings, welcome_message_image: e.target.value })}
                      disabled={!settings.welcome_message_enabled}
                    />
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        disabled={!settings.welcome_message_enabled || welcomeImageUploading}
                        onChange={(e) => handleWelcomeImageUpload(e.target.files?.[0])}
                        className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground hover:file:bg-secondary/70 file:cursor-pointer file:transition-colors"
                      />
                      {welcomeImageUploading && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1.5 shrink-0">
                          <Loader2 className="h-3 w-3 animate-spin" /> Enviando...
                        </span>
                      )}
                    </div>
                    {settings.welcome_message_image && settings.welcome_message_enabled && (
                      <div className="mt-3 rounded-lg border border-border/50 bg-secondary/20 p-3">
                        <p className="text-xs text-muted-foreground mb-2 font-medium">Pré-visualização</p>
                        <img
                          src={settings.welcome_message_image}
                          alt="Preview"
                          className="max-w-[200px] max-h-[150px] rounded-lg object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </FieldGroup>
                </SettingsSection>

                <Separator className="opacity-30" />

                {/* Closing message */}
                <SettingsSection
                  icon={X}
                  title="Mensagem de Encerramento"
                  description="Enviada quando o atendente encerra a conversa"
                  badge={
                    <Badge variant={settings.closing_message_enabled ? 'default' : 'secondary'} className="text-xs">
                      {settings.closing_message_enabled ? 'Ativa' : 'Inativa'}
                    </Badge>
                  }
                >
                  <ToggleRow
                    label="Habilitar mensagem de encerramento"
                    description="O cliente recebe esta mensagem ao final do atendimento."
                    checked={settings.closing_message_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, closing_message_enabled: checked })}
                  />

                  <FieldGroup label="Texto da mensagem">
                    <Textarea
                      placeholder="Atendimento finalizado. Obrigado pelo contato!"
                      value={settings.closing_message}
                      onChange={(e) => setSettings({ ...settings, closing_message: e.target.value })}
                      disabled={!settings.closing_message_enabled}
                      rows={2}
                      className="resize-none"
                    />
                  </FieldGroup>

                  <FieldGroup
                    label="Delay para encerrar"
                    description="Tempo de espera (em segundos) entre o clique em Encerrar e o fechamento definitivo. Permite que o cliente responda antes do encerramento."
                  >
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={0}
                        max={600}
                        value={settings.close_delay_seconds}
                        onChange={(e) => setSettings({ ...settings, close_delay_seconds: Math.max(0, Math.min(600, parseInt(e.target.value) || 0)) })}
                        className="w-28"
                      />
                      <span className="text-xs text-muted-foreground">segundos</span>
                    </div>
                  </FieldGroup>
                </SettingsSection>
              </>
            )}

            {/* ═══════ HORÁRIOS ═══════ */}
            {activeSection === 'hours' && (
              <SettingsSection
                icon={Clock}
                title="Horário de Atendimento"
                description="Configure os horários para cada dia da semana. Mensagens fora do horário recebem resposta automática."
                badge={
                  <Badge variant={settings.business_hours_enabled ? 'default' : 'secondary'} className="text-xs">
                    {settings.business_hours_enabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                }
              >
                <ToggleRow
                  label="Habilitar controle de horário"
                  description="Envia mensagem automática para clientes fora do horário comercial."
                  checked={settings.business_hours_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, business_hours_enabled: checked })}
                />

                {/* Schedule grid */}
                <div className="rounded-lg border border-border/50 overflow-hidden">
                  {DAYS_OF_WEEK.map((day, idx) => {
                    const dayKey = String(day.value);
                    const daySchedule = settings.business_hours_schedule?.[dayKey] || { enabled: false, start: "08:00", end: "18:00" };
                    const isEnabled = daySchedule.enabled && settings.business_hours_enabled;

                    return (
                      <div
                        key={day.value}
                        className={`flex items-center gap-4 px-4 py-3 transition-colors ${idx !== 0 ? 'border-t border-border/30' : ''
                          } ${isEnabled ? 'bg-primary/5' : 'bg-card/30'}`}
                      >
                        <div className="w-36 flex items-center gap-2.5">
                          <Checkbox
                            checked={daySchedule.enabled}
                            onCheckedChange={(checked) => {
                              const newSchedule = { ...settings.business_hours_schedule };
                              newSchedule[dayKey] = { ...daySchedule, enabled: checked };
                              setSettings({ ...settings, business_hours_schedule: newSchedule });
                            }}
                            disabled={!settings.business_hours_enabled}
                          />
                          <Label className={`text-sm font-medium ${!settings.business_hours_enabled ? 'opacity-40' : isEnabled ? '' : 'opacity-60'}`}>
                            {day.label}
                          </Label>
                        </div>

                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={daySchedule.start}
                            onChange={(e) => {
                              const newSchedule = { ...settings.business_hours_schedule };
                              newSchedule[dayKey] = { ...daySchedule, start: e.target.value };
                              setSettings({ ...settings, business_hours_schedule: newSchedule });
                            }}
                            disabled={!settings.business_hours_enabled || !daySchedule.enabled}
                            className="w-32 text-center"
                          />
                          <span className="text-muted-foreground text-xs">até</span>
                          <Input
                            type="time"
                            value={daySchedule.end}
                            onChange={(e) => {
                              const newSchedule = { ...settings.business_hours_schedule };
                              newSchedule[dayKey] = { ...daySchedule, end: e.target.value };
                              setSettings({ ...settings, business_hours_schedule: newSchedule });
                            }}
                            disabled={!settings.business_hours_enabled || !daySchedule.enabled}
                            className="w-32 text-center"
                          />
                        </div>

                        {!daySchedule.enabled && (
                          <Badge variant="secondary" className="text-xs opacity-60">Fechado</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>

                <FieldGroup label="Mensagem fora do horário" description="Texto enviado automaticamente quando o cliente contata fora do horário.">
                  <Textarea
                    placeholder="Mensagem enviada quando cliente entra em contato fora do horário..."
                    value={settings.business_hours_message}
                    onChange={(e) => setSettings({ ...settings, business_hours_message: e.target.value })}
                    disabled={!settings.business_hours_enabled}
                    rows={2}
                    className="resize-none"
                  />
                </FieldGroup>
              </SettingsSection>
            )}

            {/* ═══════ RESPOSTAS RÁPIDAS ═══════ */}
            {activeSection === 'quick' && (
              <SettingsSection
                icon={Zap}
                title="Respostas Rápidas"
                description="Crie atalhos para mensagens frequentes. Digite / no chat para usar."
                badge={
                  <Button size="sm" onClick={() => setShowQuickReplyDialog(true)} className="gap-1.5 h-8">
                    <Plus size={14} />
                    Nova
                  </Button>
                }
              >
                {quickReplies.length === 0 ? (
                  <div className="text-center py-12 rounded-lg border border-dashed border-border/50">
                    <Zap className="h-10 w-10 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">Nenhuma resposta rápida cadastrada</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Clique em "Nova" para criar a primeira</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {quickReplies.map((qr) => (
                      <div key={qr.id} className="flex items-start justify-between gap-3 p-3.5 rounded-lg border border-border/50 bg-card/50 hover:bg-card/80 transition-colors group">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-md font-mono">/{qr.shortcut}</code>
                            {qr.title && <span className="text-sm font-medium truncate">{qr.title}</span>}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{qr.content}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 shrink-0"
                          onClick={() => handleDeleteQuickReply(qr.id)}
                        >
                          <Trash2 size={14} className="text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </SettingsSection>
            )}

            {/* ═══════ PESQUISA DE SATISFAÇÃO ═══════ */}
            {activeSection === 'survey' && (
              <SettingsSection
                icon={Star}
                title="Pesquisa de Satisfação"
                description="Enviada automaticamente após o encerramento da conversa para medir a qualidade do atendimento"
                badge={
                  <Badge variant={settings.satisfaction_survey_enabled ? 'default' : 'secondary'} className="text-xs">
                    {settings.satisfaction_survey_enabled ? 'Ativa' : 'Inativa'}
                  </Badge>
                }
              >
                <ToggleRow
                  label="Habilitar pesquisa de satisfação"
                  description="O cliente pode avaliar o atendimento de 1 a 5 estrelas."
                  checked={settings.satisfaction_survey_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, satisfaction_survey_enabled: checked })}
                />

                <FieldGroup label="Mensagem da pesquisa">
                  <Textarea
                    value={settings.satisfaction_survey_message}
                    onChange={(e) => setSettings({ ...settings, satisfaction_survey_message: e.target.value })}
                    disabled={!settings.satisfaction_survey_enabled}
                    rows={5}
                    className="resize-none"
                  />
                </FieldGroup>

                <FieldGroup label="Mensagem de agradecimento" description="Enviada após o cliente responder a pesquisa.">
                  <Textarea
                    value={settings.satisfaction_thank_you_message}
                    onChange={(e) => setSettings({ ...settings, satisfaction_thank_you_message: e.target.value })}
                    disabled={!settings.satisfaction_survey_enabled}
                    rows={2}
                    className="resize-none"
                  />
                </FieldGroup>

                <FieldGroup label="Timeout da resposta" description="Tempo de espera (em minutos) pela resposta do cliente antes de enviar o agradecimento automaticamente.">
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={settings.satisfaction_timeout_minutes}
                      onChange={(e) => setSettings({ ...settings, satisfaction_timeout_minutes: parseInt(e.target.value) || 10 })}
                      disabled={!settings.satisfaction_survey_enabled}
                      className="w-28"
                    />
                    <span className="text-xs text-muted-foreground">minutos</span>
                  </div>
                </FieldGroup>
              </SettingsSection>
            )}

            {/* ═══════ SISTEMA ═══════ */}
            {activeSection === 'system' && (
              <>
                {/* Bot Timeout */}
                <SettingsSection
                  icon={Timer}
                  title="Timeout do BOT"
                  description="Controle o que acontece quando a conversa fica inativa no modo BOT"
                >
                  <FieldGroup label="Tempo limite (minutos)" description="Se a conversa ficar inativa no BOT por esse tempo, ela vai para Aguardando automaticamente. Use 0 para desabilitar.">
                    <div className="flex items-center gap-3">
                      <Input
                        type="number" min="0" step="1"
                        value={settings.bot_timeout_minutes}
                        onChange={(e) => setSettings({ ...settings, bot_timeout_minutes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                        className="w-28"
                      />
                      <span className="text-xs text-muted-foreground">minutos</span>
                    </div>
                  </FieldGroup>

                  {settings.bot_timeout_minutes > 0 && (
                    <div className="rounded-lg border border-border/50 p-4 space-y-3 bg-card/30">
                      <ToggleRow
                        label="Enviar mensagem no timeout"
                        description="Envia automaticamente um texto ao cliente quando a conversa sair do BOT por inatividade."
                        checked={settings.bot_timeout_message_enabled}
                        onCheckedChange={(checked) => setSettings({ ...settings, bot_timeout_message_enabled: checked })}
                        disabled={!settings.bot_timeout_minutes}
                      />
                      {settings.bot_timeout_message_enabled && (
                        <FieldGroup label="Texto da mensagem">
                          <Textarea
                            value={settings.bot_timeout_message}
                            onChange={(e) => setSettings({ ...settings, bot_timeout_message: e.target.value })}
                            disabled={!settings.bot_timeout_message_enabled || !settings.bot_timeout_minutes}
                            rows={2}
                            className="resize-none"
                          />
                        </FieldGroup>
                      )}
                    </div>
                  )}
                </SettingsSection>

                <Separator className="opacity-30" />

                {/* Attendance Timeout */}
                <SettingsSection
                  icon={Clock}
                  title="Timeout de Atendimento"
                  description="Controle o que acontece quando conversas ativas ficam sem resposta"
                >
                  <FieldGroup label="Tempo limite (minutos)" description="Se a conversa atribuída ficar sem interação por esse tempo, será finalizada automaticamente. Use 0 para desabilitar.">
                    <div className="flex items-center gap-3">
                      <Input
                        type="number" min="0" step="1"
                        value={settings.attendance_timeout_minutes}
                        onChange={(e) => setSettings({ ...settings, attendance_timeout_minutes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                        className="w-28"
                      />
                      <span className="text-xs text-muted-foreground">minutos</span>
                    </div>
                  </FieldGroup>

                  <ToggleRow
                    label="Enviar mensagem no timeout"
                    description="Envia um texto ao cliente quando a conversa for encerrada por inatividade."
                    checked={settings.timeout_message_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, timeout_message_enabled: checked })}
                  />

                  {settings.timeout_message_enabled && (
                    <FieldGroup label="Texto da mensagem">
                      <Textarea
                        value={settings.timeout_message}
                        onChange={(e) => setSettings({ ...settings, timeout_message: e.target.value })}
                        disabled={!settings.timeout_message_enabled}
                        rows={2}
                        className="resize-none"
                      />
                    </FieldGroup>
                  )}
                </SettingsSection>

                <Separator className="opacity-30" />

                {/* Promessa de pagamento */}
                <SettingsSection
                  icon={CalendarDays}
                  title="Promessa de Pagamento"
                  description="Configuração do calendário de desbloqueio"
                >
                  <FieldGroup label="Dias disponíveis" description="Quantidade de dias disponíveis no calendário de desbloqueio por promessa de pagamento.">
                    <div className="flex items-center gap-3">
                      <Input
                        type="number" min="1" max="30" step="1"
                        value={settings.promessa_dias}
                        onChange={(e) => setSettings({ ...settings, promessa_dias: Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 5)) })}
                        className="w-28"
                      />
                      <span className="text-xs text-muted-foreground">dias</span>
                    </div>
                  </FieldGroup>
                </SettingsSection>

                <Separator className="opacity-30" />

                {/* Permissões */}
                <SettingsSection
                  icon={ShieldCheck}
                  title="Permissões"
                  description="Controle de funcionalidades e acesso"
                >
                  <ToggleRow
                    label="Permitir excluir conversas"
                    description="Quando desabilitado, a opção de exclusão é removida do menu e a API bloqueia a operação."
                    checked={settings.delete_conversations_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, delete_conversations_enabled: checked })}
                  />
                </SettingsSection>
              </>
            )}

          </div>
        </main>
      </div>

      {/* ── Quick Reply Dialog ── */}
      <Dialog open={showQuickReplyDialog} onOpenChange={setShowQuickReplyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono">Nova Resposta Rápida</DialogTitle>
            <DialogDescription>
              Crie um atalho para uma mensagem frequente. Use / no chat para acessá-lo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <FieldGroup label="Atalho *">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground font-mono text-lg">/</span>
                <Input
                  placeholder="oi"
                  value={newQuickReply.shortcut}
                  onChange={(e) => setNewQuickReply({ ...newQuickReply, shortcut: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  className="font-mono"
                />
              </div>
            </FieldGroup>
            <FieldGroup label="Título (opcional)">
              <Input
                placeholder="Saudação inicial"
                value={newQuickReply.title}
                onChange={(e) => setNewQuickReply({ ...newQuickReply, title: e.target.value })}
              />
            </FieldGroup>
            <FieldGroup label="Conteúdo *">
              <Textarea
                placeholder="Olá! Como posso ajudar?"
                value={newQuickReply.content}
                onChange={(e) => setNewQuickReply({ ...newQuickReply, content: e.target.value })}
                rows={3}
                className="resize-none"
              />
            </FieldGroup>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowQuickReplyDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateQuickReply} disabled={savingQuickReply} className="gap-2">
              {savingQuickReply && <Loader2 className="h-4 w-4 animate-spin" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
